import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/base/PageLayout';

const PrivateButler: React.FC = () => {
  useEffect(() => {
    document.title = 'プライベート執事サービス - 専属執事による最高級パーソナルサービス｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが提供するプライベート執事サービス。特別なディナーパーティー、ホームパーティー、記念日のイベント、VIPゲストのおもてなしなど、様々なシーンで専属執事が最高級のサービスを提供いたします。テーブルセッティング、料理のサービス、ワインの選定、空間演出まで、執事ならではの細やかな心配りで特別な時間を演出。個人宅への手配も承ります。');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "プライベート執事サービス",
      "provider": {
        "@type": "Person",
        "name": "執事ベル",
        "jobTitle": "全日本執事協会 3代目会長"
      },
      "areaServed": {
        "@type": "Country",
        "name": "日本"
      },
      "description": "専属執事による最高級パーソナルサービス。日常生活サポート、イベント運営、旅行アテンド、エグゼクティブアシスタント、ハウスキーピングなど、お客様のライフスタイルに合わせた完全オーダーメイドのサービスを提供"
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      title: '日常生活サポート',
      description: 'お客様の日常をより豊かにするための総合的なサポート',
      features: [
        '家事全般の管理・監督',
        'スケジュール管理・調整',
        '来客対応・おもてなし',
        '買い物代行・手配',
        '郵便物・書類管理',
        '各種予約・手配業務'
      ],
      icon: 'ri-home-4-line',
      image: '/images/generated/seq-daily-support-butler-1-62fe23.jpg'
    },
    {
      title: 'イベント運営',
      description: 'パーティーや会食などの企画から運営まで完全サポート',
      features: [
        'ホームパーティー企画・運営',
        'テーブルセッティング',
        'ゲスト対応・接遇',
        '会場コーディネート',
        '料理・飲料のサービス',
        'イベント後の片付け'
      ],
      icon: 'ri-calendar-event-line',
      image: '/images/generated/seq-event-butler-2-80fb86.jpg'
    },
    {
      title: '旅行アテンド',
      description: '国内外の旅行に同行し、快適で安心な旅をサポート',
      features: [
        '旅行プランニング・手配',
        '現地アテンド・案内',
        '通訳・翻訳サービス',
        'レストラン・観光予約',
        '荷物管理・運搬',
        '緊急時対応・サポート'
      ],
      icon: 'ri-plane-line',
      image: '/images/generated/seq-travel-butler-3-fb5f70.jpg'
    },
    {
      title: 'エグゼクティブアシスタント',
      description: 'ビジネスシーンでの秘書業務とパーソナルサポート',
      features: [
        'アポイント調整・管理',
        '資料作成・整理',
        '電話・メール対応',
        '会議準備・サポート',
        'ビジネスディナー手配',
        '機密情報管理'
      ],
      icon: 'ri-briefcase-line',
      image: '/images/generated/seq-executive-butler-4-ce1287.jpg'
    },
    {
      title: 'ハウスキーピング',
      description: '邸宅全体の管理と維持を最高水準で実現',
      features: [
        '清掃・整理整頓の監督',
        '衣類管理・メンテナンス',
        '家具・調度品の手入れ',
        '季節ごとの衣替え',
        '在庫管理・発注',
        '設備点検・業者手配'
      ],
      icon: 'ri-building-line',
      image: '/images/generated/seq-housekeeping-butler-5-007fbe.jpg'
    },
    {
      title: '紅茶サービス',
      description: '本格的な英国式ティーサービスで優雅なひとときを演出',
      features: [
        '茶葉セレクション・提案',
        '正統派ティーセレモニー',
        'アフタヌーンティー演出',
        'ティーペアリング提案',
        '茶器の管理・手入れ',
        '紅茶購入アドバイス'
      ],
      icon: 'ri-goblet-line',
      image: '/images/generated/seq-tea-butler-6-e68f76.jpg'
    },
    {
      title: 'パーソナルショッピング',
      description: 'お客様のスタイルに合わせた買い物代行とスタイリング',
      features: [
        '衣類・アクセサリー選定',
        'ギフト選び・手配',
        '食材・日用品調達',
        'ブランド品購入代行',
        'スタイリング提案',
        '返品・交換対応'
      ],
      icon: 'ri-shopping-bag-line',
      image: '/images/generated/seq-shopping-butler-7-e56b83.jpg'
    },
    {
      title: 'ゲストリレーション',
      description: 'VIPゲストへの最高級のおもてなしと対応',
      features: [
        'ゲスト情報管理',
        '空港送迎手配',
        'ウェルカムサービス',
        '滞在中のサポート',
        '観光・エンターテイメント手配',
        'お見送りサービス'
      ],
      icon: 'ri-user-star-line',
      image: '/images/generated/seq-guest-butler-8-2d573c.jpg'
    }
  ];

  const serviceScenes = [
    {
      title: 'ディナーパーティー',
      description: 'ご自宅でのディナーパーティーを、一流レストラン以上のクオリティで演出いたします。',
      details: [
        'メニュー提案とテーブルコーディネート',
        'ゲストのお出迎えからお見送りまで',
        '料理とワインの完璧なサービング',
        '会話の進行とタイミング管理'
      ]
    },
    {
      title: '記念日・特別な日',
      description: '誕生日、結婚記念日など、大切な日を特別な思い出にするお手伝いをいたします。',
      details: [
        'サプライズ演出の企画・実施',
        '空間装飾とアンビエンス作り',
        'ギフトの手配と演出',
        '写真撮影のサポート'
      ]
    },
    {
      title: 'ビジネスエンターテイメント',
      description: '重要なビジネスパートナーとの会食や接待を、完璧にサポートいたします。',
      details: [
        'プライベート空間での接待',
        'ビジネスマナーに則った対応',
        '機密保持の徹底',
        'スムーズな進行管理'
      ]
    },
    {
      title: '長期滞在サポート',
      description: '別荘や長期滞在先での生活を、快適で充実したものにいたします。',
      details: [
        '到着前の準備と環境整備',
        '滞在中の日常サポート',
        '地域情報の提供と手配',
        '帰宅後の片付けと管理'
      ]
    }
  ];

  const butlerQualities = [
    {
      icon: 'ri-eye-line',
      title: '先読みの力',
      description: 'お客様のニーズを言葉にされる前に察知し、最適なタイミングでサービスを提供いたします。'
    },
    {
      icon: 'ri-shield-check-line',
      title: '絶対的な信頼',
      description: '機密保持を徹底し、お客様のプライバシーを最優先に守ります。'
    },
    {
      icon: 'ri-star-line',
      title: '完璧な仕上がり',
      description: 'どんな細部も見逃さず、常に最高水準のサービスを維持いたします。'
    },
    {
      icon: 'ri-heart-line',
      title: '心からのおもてなし',
      description: '形式的なサービスではなく、真心を込めたおもてなしを提供いたします。'
    },
    {
      icon: 'ri-time-line',
      title: '柔軟な対応力',
      description: '予期せぬ状況にも冷静に対処し、常に最善の解決策を見出します。'
    },
    {
      icon: 'ri-global-line',
      title: '国際的な視野',
      description: '世界各国の文化とマナーを理解し、多様なゲストに対応いたします。'
    }
  ];

  return (
    <PageLayout hero={<>
<section className="relative py-24 md:py-32 bg-cover bg-center" style={{
          backgroundImage: 'url(/images/generated/seq-private-butler-hero-hands-b46a9f.jpg)'
        }}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-3xl md:text-6xl font-serif mb-6 md:mb-8 tracking-wider">
                プライベート執事サービス
              </h1>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6 md:mb-8"></div>
              <p className="text-base md:text-xl leading-relaxed max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
                お客様だけの専属執事として<br className="md:hidden" />
                最高級のパーソナルサービスをお届けします
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Introduction */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-6 md:mb-8 tracking-wider">
                  真の執事サービスを<br />
                  あなたの日常に
                </h2>
                <p className="text-sm md:text-lg text-gray-700 mb-5 md:mb-6 leading-relaxed">
                  全日本執事協会3代目会長として、執事文化の本質を追求してまいりました。プライベート執事サービスは、お客様一人ひとりのライフスタイルに合わせた完全オーダーメイドのサービスです。
                </p>
                <p className="text-sm md:text-lg text-gray-700 mb-5 md:mb-6 leading-relaxed">
                  日常の些細なことから重要なイベントまで、すべてをお任せいただき、より豊かで充実した時間をお過ごしください。執事という存在は、単なる使用人ではなく、お客様の人生をより良くするためのパートナーです。
                </p>
                <p className="text-sm md:text-lg text-gray-700 leading-relaxed">
                  英国で培った伝統的な執事の技術と、日本のおもてなしの心を融合させた、唯一無二のサービスをご提供いたします。
                </p>
              </div>
              <div className="relative">
                <div className="relative h-96 md:h-[500px] w-full">
                  <img
                    src="/images/generated/seq-butler-intro-hands-43932c.jpg"
                    alt="執事の手元"
                    className="w-full h-full object-cover object-center rounded-md shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-4 tracking-wider">
                サービス内容
              </h2>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6"></div>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
                お客様のニーズに合わせた幅広いサービスをトータルでご提供
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-md shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className="relative h-56 md:h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-amber-100 rounded-full mr-4">
                        <i className={`${service.icon} text-lg md:text-xl text-amber-700`}></i>
                      </div>
                      <h3 className="text-lg md:text-2xl font-serif text-gray-900 tracking-wider">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-xs md:text-base text-gray-600 mb-5 md:mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start text-gray-700">
                          <div className="w-1.5 h-1.5 bg-amber-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-xs md:text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Scenes */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-4 tracking-wider">
                ご利用シーン
              </h2>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6"></div>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
                様々なシーンで最高級のサービスをご提供いたします
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {serviceScenes.map((scene, index) => (
                <div key={index} className="bg-gradient-to-br from-amber-50 to-white p-6 md:p-8 rounded-md shadow-md hover:shadow-lg transition-all duration-300">
                  <h3 className="text-lg md:text-2xl font-serif text-gray-900 mb-4 md:mb-5 tracking-wide">
                    {scene.title}
                  </h3>
                  <p className="text-xs md:text-base text-gray-700 mb-5 md:mb-6 leading-relaxed">
                    {scene.description}
                  </p>
                  <ul className="space-y-2 md:space-y-3">
                    {scene.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start text-gray-700">
                        <i className="ri-check-line text-amber-600 mr-3 mt-0.5 flex-shrink-0"></i>
                        <span className="text-xs md:text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Butler Qualities */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-4 tracking-wider">
                執事の心得
              </h2>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6"></div>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
                プロフェッショナル執事として大切にしている6つの心得
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {butlerQualities.map((quality, index) => (
                <div key={index} className="text-center bg-white p-6 md:p-8 rounded-md shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-4 md:mb-5 bg-gradient-to-br from-amber-100 to-amber-50 rounded-full">
                    <i className={`${quality.icon} text-xl md:text-2xl text-amber-700`}></i>
                  </div>
                  <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 md:mb-4 tracking-wide">
                    {quality.title}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                    {quality.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-4 tracking-wider">
                サービス開始までの流れ
              </h2>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6"></div>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
                お問い合わせから実施まで、丁寧にサポートいたします
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-10">
              {[
                {
                  step: '01',
                  title: 'お問い合わせ',
                  description: 'まずはお気軽にご相談ください。ご要望を詳しくお聞かせいただきます。'
                },
                {
                  step: '02',
                  title: 'ヒアリング',
                  description: 'お客様のライフスタイルやニーズを詳細にお伺いいたします。'
                },
                {
                  step: '03',
                  title: 'プラン作成',
                  description: 'お客様専用のカスタマイズされたサービスプランをご提案いたします。'
                },
                {
                  step: '04',
                  title: 'サービス開始',
                  description: '専属執事による最高級のサービスを開始いたします。'
                }
              ].map((process, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-5 bg-gradient-to-br from-amber-700 to-amber-800 text-white rounded-full shadow-lg">
                    <span className="text-xl md:text-2xl font-bold tracking-wider">{process.step}</span>
                  </div>
                  <h3 className="text-lg md:text-xl font-serif text-gray-900 mb-4 tracking-wide">
                    {process.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed px-2">
                    {process.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-amber-700 via-amber-800 to-amber-700 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-4xl font-serif text-white mb-6 md:mb-8 tracking-wider">
              あなただけの執事サービス
            </h2>
            <div className="w-16 h-0.5 bg-white/50 mx-auto mb-6 md:mb-8"></div>
            <p className="text-base md:text-xl text-amber-50 mb-8 md:mb-10 leading-relaxed px-5 sm:px-6 lg:px-8">
              お客様のご要望に合わせた最適なサービスプランをご提案いたします<br className="hidden md:block" />
              まずはお気軽にお問い合わせください
            </p>
            <Link
              to="/contact"
              className="btn-gold inline-block bg-white text-amber-800 px-8 md:px-10 py-4 md:py-5 rounded-md font-medium hover:bg-amber-50 transition-all duration-300 whitespace-nowrap text-sm md:text-base tracking-wider shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              お問い合わせフォーム
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default PrivateButler;
